import {Injectable} from '@angular/core';
import * as XLSX from 'xlsx';

export type BulkParseResult = {
  validNames: string[];
  invalid: { row: number; reason: string }[];
  duplicates: string[]; // duplicados detectados dentro del archivo
};

@Injectable({providedIn: 'root'})
export class BulkParseService {
  // Lee .xlsx o .csv, primera hoja, y valida para "crear" (solo columna nombre)
  async parseCreateFile(file: File): Promise<BulkParseResult> {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, {type: 'array'});

    const sheetName = wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];

    const invalid: BulkParseResult['invalid'] = [];

    // 1) Validar título  "Carga de datos"
    if (!this.isValidTitle(ws)) {
      invalid.push({row: 1, reason: 'Título "Carga de datos" no encontrado en A1'});
    }

    // 2) Buscar fila de encabezados con 'nombre'
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, {header: 1, blankrows: false});
    if (!rows || rows.length === 0) {
      return {validNames: [], invalid: [...invalid, {row: 1, reason: 'Hoja vacía'}], duplicates: []};
    }

    const {headerIndex, nombreCol} = this.findHeaderRow(rows);

    if (headerIndex === -1) {
      return {
        validNames: [],
        invalid: [...invalid, {
          row: 1,
          reason: 'Formato inválido. Usa la plantilla oficial: debe existir una fila de encabezados con la columna "Nombre".'
        }],
        duplicates: [],
      };
    }

    // 3) Leer filas de datos desde headerIndex+1
    const processed = this.processRows(rows, headerIndex, nombreCol);
    invalid.push(...processed.invalid);

    // 4) Deduplicar internos (case-insensitive)
    const {valid, duplicates} = this.deduplicateNames(processed.names);

    return {validNames: valid, invalid, duplicates};
  }

  private isValidTitle(ws: XLSX.WorkSheet): boolean {
    const a1 = (ws['A1']?.v ?? '')
      .toString()
      .trim()
      .toLowerCase()
        .replaceAll(/\s+/g, ' ');
    return a1.includes('carga de datos');
  }

  private findHeaderRow(rows: any[][]): { headerIndex: number; nombreCol: number } {
    // Normaliza textos: NBSP -> espacio, quita tildes, trim, lower, colapsa espacios
    const norm = (s: any) =>
      String(s ?? '')
          .replaceAll('\u00A0', ' ')
        .normalize('NFD')
          .replaceAll(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
          .replaceAll(/\s+/g, ' ');

    // Permitimos varias filas "decorativas" (logo, título, notas)
    const MAX_SCAN = Math.min(rows.length, 30);

    for (let i = 0; i < MAX_SCAN; i++) {
      const r = rows[i] || [];
      const idxNombre = r.findIndex((c: any) => norm(c) === 'nombre');

      if (idxNombre !== -1) {
        return {headerIndex: i, nombreCol: idxNombre};
      }
    }
    return {headerIndex: -1, nombreCol: -1};
  }

  private processRows(rows: any[][], headerIndex: number, nombreCol: number): {
    names: string[],
    invalid: { row: number; reason: string }[]
  } {
    const names: string[] = [];
    const invalid: { row: number; reason: string }[] = [];

    for (let i = headerIndex + 1; i < rows.length; i++) {
      const r = rows[i] || [];
      const rawNombre = r[nombreCol];

      const emptyNombre = rawNombre === undefined || rawNombre === null || String(rawNombre).trim() === '';
      if (emptyNombre) continue;

      const normalized = this.normalizeName(String(rawNombre ?? ''));

      if (!normalized) {
        invalid.push({row: i + 1, reason: 'nombre vacío o solo espacios'});
        continue;
      }
      if (normalized.length > 255) {
        invalid.push({row: i + 1, reason: 'nombre > 255 caracteres'});
        continue;
      }

      names.push(normalized);
    }
    return {names, invalid};
  }

  private deduplicateNames(nombres: string[]): { valid: string[]; duplicates: string[] } {
    const seen = new Set<string>();
    const valid: string[] = [];
    const duplicates: string[] = [];

    for (const n of nombres) {
      const key = n.toLowerCase();
      if (seen.has(key)) {
        duplicates.push(n);
      } else {
        seen.add(key);
        valid.push(n);
      }
    }
    return {valid, duplicates};
  }

  private normalizeName(name: string): string {
    return name.replaceAll(/\s+/g, ' ').trim();
  }
}
